<template>
    <div class="flex flex-col flex-grow p-6 bg-dark-1 overflow-y-auto">
        <OrganizationsMenu/>
        <div class="mt-5 flex-grow flex flex-col">
            <nav class="flex-1 px-2 bg-dark-1" aria-label="Sidebar">
                <template v-for="item in navigation" :key="item.name">
                  <div v-if="!item.openInNewTab">
                    <div v-if="!item.children" class="my-6">
                      <router-link :to="item.href"
                                   :class="[route.path === item.href ? 'text-white' : ' text-default-3', 'bg-dark-1 flex flex-row items-center text-body-6']">
                        <component :is="item.icon" :color="$route.path === item.href  ? colorActive : colorDefault" class="mr-2.5 h-3.5 w-3.5"
                                   aria-hidden="true" />
                        {{ item.name }}
                      </router-link>
                    </div>
                    <Disclosure v-else as="div"  class=" my-6" v-slot="{ open }">
                      <DisclosureButton
                          :class="[$route.path === item.href ? 'text-white' : 'text-default-3', ' group w-full flex items-center text-left text-sm font-normal focus:outline-none ']">
                        <component :is="item.icon" :color="$route.path === item.href  ? colorActive : colorDefault" class="mr-2.5 h-3.5 w-3.5"
                                   aria-hidden="true" />
                        <span class="flex-1">
                              {{ item.name }}
                            </span>
                        <img v-if="open" src="../../assets/icons/chevron-up.svg" alt="close" />
                        <img v-else src="../../assets/icons/chevron-down.svg" alt="open"/>
                      </DisclosureButton>
                      <DisclosurePanel class="">
                        <template v-for="subItem in item.children" :key="subItem.name">
                          <router-link v-if="!subItem.openInNewTab" :to="subItem.href"
                                       :class="[$route.path === subItem.href  ? 'text-white' : 'text-default-3', ' group w-full flex items-center text-left text-sm font-normal focus:outline-none ml-6 my-4']">
                            {{ subItem.name }}
                          </router-link>
                          <div v-else @click="goToWebsite(subItem.url)"
                               class="text-default-3 group w-full flex items-center text-left text-sm font-normal focus:outline-none ml-6 my-4 cursor-pointer">
                            {{ subItem.name }}
                          </div>
                        </template>
                      </DisclosurePanel>
                    </Disclosure>
                  </div>
                  <div v-else @click="goToWebsite(item.url)"
                       class="text-default-3 bg-dark-1 flex flex-row items-center text-body-6 cursor-pointer">
                    <component :is="item.icon" :color="$route.path === item.href  ? colorActive : colorDefault" class="mr-2.5 h-3.5 w-3.5"
                               aria-hidden="true" />
                    {{item.name}}
                  </div>

                    <hr v-if="item.hasSeparator" class="h-px bg-background-3 opacity-20 mt-6 mb-6" />
                </template>
            </nav>
        </div>
        <div class="flex flex-col mt-9">
            <CoreButton v-if="websiteURL" class="mb-10" buttonType="outline-white" @click="goToWebsite(websiteURL)">
               <img src="@/assets/icons/global-white.svg" alt="global icon" class="w-6 h-6 mr-2">
               Go to org website
            </CoreButton>
            <img class="h-8" src="@/assets/icons/logo/white/horizontal_powered_by.svg" alt="Workflow" />
        </div>
    </div>
</template>
<script setup>
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue'
import CalendarIcon from '../icons/sidebar/Calendar.vue'
import ChatIcon from '../icons/sidebar/Chat.vue'
import HomeIcon from '../icons/sidebar/Home.vue'
import MailIcon from '../icons/sidebar/Mail.vue'
import UsersIcon from '../icons/sidebar/Users.vue'
import BellIcon from '../icons/sidebar/Bell.vue'
import HelpIcon from '../icons/sidebar/Help.vue'
import FolderIcon from '../icons/sidebar/Folder.vue'
import DocumentIcon from '../icons/sidebar/Document.vue'
import CardIcon from '../icons/sidebar/Card.vue'
import ShieldIcon from '../icons/sidebar/Shield.vue'
import CommandIcon from '../icons/sidebar/Command.vue'
import {useAuth} from "@/stores/AuthStore";
import {storeToRefs} from "pinia";
import {computed, onBeforeMount, watch} from "vue";
import {useRoute} from "vue-router";
import OrganizationsMenu from "@/components/menus/Organizations.vue";
import ReceiptIcon from "@/components/icons/sidebar/Receipt.vue";
import {useCheckFeature} from "@/composables/useCheckFeature";
import CoreButton from "@/components/core/Button.vue";

const {loadMemberFeatures, memberFeatures, canAccessBenefitDefinitions, canAccessLifePolicies} = useCheckFeature();

const colorActive = '#ffffff';
const colorDefault = '#A6B0CF';

const authStore = useAuth();
const {isAdmin, isSuperAdmin, isContentAdmin, isRepresentative, isGroupAdmin, member, organization, hasPaymentsActive, hasSplitPaymentsActive, hasLineItemOptionsActive} = storeToRefs(authStore);

const route = useRoute();

const websiteURL = computed(() => {
    return organization.value.website_url;
});

const FINVO_ADMIN_URL =  "connectplusinvoice.com/admin/authentication";
const FINVO_MEMBER_URL = "connectplusinvoice.com/authentication";
const SUPPORT_CENTER_URL = 'https://www.nepservices.com/support/connect-plus-support'

const goToWebsite = (url) => {
    if (!/^https?:\/\//i.test(url)) {
        url = 'https://' + url;
    }

    window.open(url, '_blank');
}

const navigationAdmin = [
    {
        name: 'Dashboard',
        icon: HomeIcon,
        current: false,
        href: '/'
    },
    {
        name: 'Calendar',
        icon: CalendarIcon,
        current: false,
        href: '/calendar'
    },
    {
        name: 'News',
        icon: ReceiptIcon,
        current: false,
        href: '/news'
    },
    {
        name: 'Email & SMS',
        icon: MailIcon,
        current: false,
        href: '/inbox',
    },
    {
        name: 'Members',
        icon: UsersIcon,
        current: false,
        children: [
            { name: 'Members List', href: '/members' },
            { name: 'Groups', href: '/groups'},
            { name: 'Profile builder', href: '/profile-builder'},
            { name: 'Field automations', href: '/field-automations' },
            { name: 'Import and invite', href: '/import' },
        ],
    },
    {
        name: 'Resources',
        icon: FolderIcon,
        current: false,
        href: '/resources',
    },
    {
        name: 'Documents',
        icon: DocumentIcon,
        current: false,
        href: '/documents',
    },
    {
        name: 'Benefits',
        icon: ShieldIcon,
        current: false,
        children: [
            { name: 'Benefits Dashboard', href: '/benefits-dashboard' },
            { name: 'Enrollments Dashboard', href: '/enrollments-dashboard' },
            { name: 'Benefit Definitions', href: '/benefit-definitions' },
            { name: 'Life Policies', href: '/life-policies' },
        ],
    },
    {
        name: 'Payments Overview',
        icon: CardIcon,
        current: false,
        hasSeparator: true,
        children: [
            { name: 'Payments', url: FINVO_ADMIN_URL, openInNewTab: true },
            { name: 'Payments Log', href: '/payments' },
            { name: 'Payment Definitions', href: '/payment-definitions' },
        ],
    },
    {
        name: 'System Notifications',
        icon: BellIcon,
        current: false,
        href: '/notifications',
    },
    {
        name: 'Support Center',
        icon: HelpIcon,
        url: SUPPORT_CENTER_URL,
        openInNewTab: true,
    },
    {
        name: 'How-to Guide',
        icon: HelpIcon,
        current: false,
        href: '/how-to-guide',
    }
];

const navigationContentAdmin = [
    {
        name: 'Dashboard',
        icon: HomeIcon,
        current: false,
        href: '/'
    },
    {
        name: 'Calendar',
        icon: CalendarIcon,
        current: false,
        href: '/calendar'
    },
    {
        name: 'News',
        icon: ReceiptIcon,
        current: false,
        href: '/news'
    },
    {
        name: 'Email & SMS',
        icon: MailIcon,
        current: false,
        href: '/inbox',
    },
    {
        name: 'Resources',
        icon: FolderIcon,
        current: false,
        href: '/resources',
    },
    {
        name: 'Documents',
        icon: DocumentIcon,
        current: false,
        href: '/documents',
        hasSeparator: true
    },
    {
        name: 'System Notifications',
        icon: BellIcon,
        current: false,
        href: '/notifications',
        hasSeparator: true,
    },
    {
        name: 'How-to Guide',
        icon: HelpIcon,
        current: false,
        href: '/how-to-guide',
    },
];

const navigationRepresentative = [
    {
        name: 'Dashboard',
        icon: HomeIcon,
        current: false,
        href: '/'
    },
    {
        name: 'Calendar',
        icon: CalendarIcon,
        current: false,
        href: '/calendar'
    },
    {
        name: 'News',
        icon: ReceiptIcon,
        current: false,
        href: '/news'
    },
    {
        name: 'Email & SMS',
        icon: MailIcon,
        current: false,
        href: '/inbox',
    },
    {
        name: 'Members',
        icon: UsersIcon,
        current: false,
        children: [
            { name: 'Members List', href: '/members' },
            { name: 'Groups', href: '/groups'},
        ],
    },
    {
        name: 'Resources',
        icon: FolderIcon,
        current: false,
        href: '/resources',
    },
    {
        name: 'Documents',
        icon: DocumentIcon,
        current: false,
        href: '/documents',
        hasSeparator: true
    },
    {
        name: 'System Notifications',
        icon: BellIcon,
        current: false,
        href: '/notifications',
        hasSeparator: true,
    },
    {
        name: 'How-to Guide',
        icon: HelpIcon,
        current: false,
        href: '/how-to-guide',
    },
];

const navigationRestricted = [
    {
        name: 'Profile',
        icon: UsersIcon,
        current: false,
        href: '/profile'
    },
];

const navigationMember = [
    {
        name: 'Dashboard',
        icon: HomeIcon,
        current: false,
        href: '/'
    },
    {
        name: 'Calendar',
        icon: CalendarIcon,
        current: false,
        href: '/calendar'
    },
    {
        name: 'News',
        icon: ReceiptIcon,
        current: false,
        href: '/news'
    },
    {
        name: 'Email & SMS',
        icon: MailIcon,
        current: false,
        href: '/inbox',
    },
    {
        name: 'Resources',
        icon: FolderIcon,
        current: false,
        href: '/resources',
    },
    {
        name: 'Documents',
        icon: DocumentIcon,
        current: false,
        href: '/documents',
        hasSeparator: !hasPaymentsActive,
    },
    {
        name: 'Payments',
        icon: CardIcon,
        current: false,
        url: FINVO_MEMBER_URL,
        hasSeparator: true,
        openInNewTab: true,
    },
    {
        name: 'System Notifications',
        icon: BellIcon,
        current: false,
        href: '/notifications',
    },
    {
        name: 'How-to Guide',
        icon: HelpIcon,
        current: false,
        href: '/how-to-guide',
    },
];

const features = [{
    name: 'Chat',
    icon: ChatIcon,
    current: false,
    href: '/chat'
}]

const groupAdminHiddenItems = [
    'Benefits Dashboard',
    'Benefit Definitions',
    'Import and invite',
    'Profile builder',
    'Field automations',
];

const navigation = computed(() => {
    let nav = [];

    if (isAdmin.value) {
        nav = [...navigationAdmin];
    } else if (isGroupAdmin.value) {
        nav = navigationAdmin
            .filter(item => !groupAdminHiddenItems.includes(item.name))
            .map(item => {
                if (item.children) {
                    return {
                        ...item,
                        children: item.children.filter(child => !groupAdminHiddenItems.includes(child.name)),
                    };
                }
                return item;
            });
    } else if (isRepresentative.value) {
        nav = [...navigationRepresentative];
    } else if (isContentAdmin.value) {
        nav = [...navigationContentAdmin];
    } else {
        if (member.value && member.value.status === 'approved') {
            nav = [...navigationMember];
        } else {
            return nav = [...navigationRestricted];
        }
    }

    if (memberFeatures.value) {
        const featureNav = [];
        Object.keys(memberFeatures.value).forEach((feature) => {
            if (memberFeatures.value[feature]) {
                const aux = features.find(f => f.name.toLowerCase() === feature);
                if (aux) {
                    featureNav.push(aux);
                }
            }
        });

        nav = [...nav.slice(0, 2), ...featureNav, ...nav.slice(2)];
    }

    // Health surfaces — only health-capable admins.
    if (!canAccessBenefitDefinitions.value) {
        nav = nav.map(item => {
            if (item.children) {
                return {
                    ...item,
                    children: item.children.filter(child =>
                        child.name !== 'Benefit Definitions' && child.name !== 'Benefits Dashboard'
                    ),
                };
            }
            return item;
        });
    }

    // Life surfaces — only life-capable admins.
    if (!canAccessLifePolicies.value) {
        nav = nav.map(item => {
            if (item.children) {
                return {
                    ...item,
                    children: item.children.filter(child =>
                        child.name !== 'Life Policies' && child.name !== 'Enrollments Dashboard'
                    ),
                };
            }
            return item;
        });
    }

    if (!hasPaymentsActive.value) {
        nav = nav.filter(item => item.name !== 'Payments Overview' && item.name !== 'Payments');
    }

    if (!hasSplitPaymentsActive.value && !hasLineItemOptionsActive.value) {
        nav = nav.map(item => {
            if (item.name === 'Payments Overview') {
                return {
                    ...item,
                    children: item.children.filter(child => child.name !== 'Payment Definitions'),
                };
            }
            return item;
        });
    }

    if (!isSuperAdmin.value) {
        nav = nav.filter(item => item.name !== 'How-to Guide');
        nav = nav.map(item => {
            if (item.children) {
                return {
                    ...item,
                    children: item.children.filter(child => child.name !== 'Field automations')
                };
            }
            return item;
        });
    }

    nav = nav.filter(item => !item.children || item.children.length > 0);

    const sysNotifIdx = nav.findIndex(item => item.name === 'System Notifications');
    if (sysNotifIdx > 0) {
        nav[sysNotifIdx - 1] = { ...nav[sysNotifIdx - 1], hasSeparator: true };
    }

    if (isSuperAdmin.value && !isGroupAdmin.value && organization.value?.slug) {
        nav.push({
            name: 'Superadmin Hub',
            icon: CommandIcon,
            current: false,
            url: `https://superadmin${window.appConfig.DOMAIN}/organization-menu/${organization.value.slug}`,
            openInNewTab: true,
        });
    }

    return nav;
});

onBeforeMount(() => {
    loadMemberFeatures(organization.value.slug);
})

watch(() => route.path, (newRoute, oldRoute) => {
    if (newRoute !== oldRoute) {
        loadMemberFeatures(organization.value.slug);
    }
});
</script>
